import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { apiFetch } from '@/lib/api-fetch.js';

/**
 * Client for the Microsoft Entra group typeahead (`GET /v1/directory/groups/search`, served by
 * `cti-identity-service` and routed through the gateway). It is the group half of what
 * `lib/api/users.ts` does for people — same debounce, same blank-term short circuit, same
 * any-authenticated-user reach, because both are pickers and neither is administration.
 *
 * Two things are contract rather than convenience:
 *
 * - `member_count` is `number | null`, and a null stays a null. "We could not count this group" and
 *   "this group is empty" are different facts, so nothing here defaults the count to zero.
 * - An unreachable or unconfigured directory answers **502**, deliberately not 503 — several clients
 *   in this SPA swallow a 503 as maintenance-mode noise. So this module lets the failure surface as an
 *   ordinary query error with no toast and no status special-casing, which is what lets the group picker
 *   show its own message and a retry while the rest of the share dialog carries on.
 */

const API_BASE = '/v1/directory/groups';

/** One Entra group as the picker draws it. `memberCount` is null when the directory could not count it. */
export interface DirectoryGroup {
  id: string;
  name: string;
  memberCount: number | null;
}

/** The wire row, snake_case exactly as the service sends it. */
interface DirectoryGroupWire {
  id: string;
  name: string;
  member_count: number | null;
}

/**
 * Search the group directory. A blank term short-circuits to `[]` (the server answers the same, but
 * skipping the round trip keeps the typeahead quiet until someone types). `limit` defaults to 20; the
 * server caps it at 50.
 */
async function searchDirectoryGroups(
  term: string,
  limit = 20,
): Promise<DirectoryGroup[]> {
  const q = term.trim();
  if (!q) return [];
  const params = new URLSearchParams({ q });
  params.set('limit', String(limit));
  const res = await apiFetch(`${API_BASE}/search?${params.toString()}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`Failed to search groups (${res.status})`);
  const wire = (await res.json()) as DirectoryGroupWire[];
  return wire.map((group) => ({
    id: group.id,
    name: group.name,
    memberCount: group.member_count ?? null,
  }));
}

/** Query-key factory so the typeahead cache is keyed by the exact term + limit. */
export const directoryGroupKeys = {
  search: (term: string, limit: number) =>
    ['directory-groups', 'search', term.trim(), limit] as const,
};

/** TanStack Query factory for one group search; disabled while the term is blank. */
export function directoryGroupsSearchQueryOptions(term: string, limit = 20) {
  return queryOptions({
    queryKey: directoryGroupKeys.search(term, limit),
    queryFn: () => searchDirectoryGroups(term, limit),
    enabled: term.trim().length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Debounced group search hook for a typeahead input, mirroring `useUserSearch`: the raw term settles for
 * `delayMs` before it reaches the query, so a request goes out per search rather than per keystroke.
 */
export function useDirectoryGroupSearch(
  term: string,
  limit = 20,
  delayMs = 250,
): UseQueryResult<DirectoryGroup[], Error> {
  const [debounced, setDebounced] = useState(term);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(term), delayMs);
    return () => clearTimeout(handle);
  }, [term, delayMs]);
  return useQuery(directoryGroupsSearchQueryOptions(debounced, limit));
}

/**
 * Group names this session has picked, by object id.
 *
 * A share view never carries a group's name: identity's batch directory lookup is a USER directory and
 * cannot resolve a group object id, resolving one would put a Graph call on the path that renders a share
 * view, and nothing caches the name because a cached copy of a name Entra owns goes stale on the first
 * rename. The only place the name exists, then, is the picker the author just used — so the picker
 * remembers it, and the shares it creates read back by name instead of by id.
 *
 * Session-lifetime and never a source of truth: after a reload a share falls back to naming its recipient
 * as a group. That is why nothing here expires or persists — a wrong name would be worse than none.
 */
const pickedGroupNames = new Map<string, string>();

/** Remember a group the author picked, so a share addressed to it can be named afterwards. */
export function rememberGroupName(group: DirectoryGroup): void {
  pickedGroupNames.set(group.id, group.name);
}

/** The remembered name of a group, or null when this session never picked it. */
export function rememberedGroupName(groupId: string): string | null {
  return pickedGroupNames.get(groupId) ?? null;
}

/**
 * How many people are in a group, in the words the picker shows.
 *
 * The count exists to answer "big or small" without expanding 47 names, so an unknown count says so
 * outright: rendering it as `0 people` would claim the group is empty, which is a different — and
 * wrong — fact about somebody's audience.
 */
export function groupMemberCountLabel(memberCount: number | null): string {
  if (memberCount === null) return 'members unknown';
  return memberCount === 1 ? '1 person' : `${memberCount} people`;
}
